import { differenceInMinutes } from 'date-fns';

import {
  adjustWantedTime,
  getParticipantLineItemGroups,
  type OrderingContext,
  prepareLedger,
} from '@order/features-ordering';
import {
  getSharedLineItemAttribution,
  getSharedLineItemCartLineItemTelemetry,
} from '@order/shared-line-item';
import { type TelemetryEvent, telemetryProductFromInput } from '@order/Telemetry';
import { convertPriceToFloat } from '@order/utils';

import {
  checkIfBundleCartLineItem,
  getBundleAttribution,
} from '../../../helpers/bundle-attribution';

export function formatTelemetryBag(context: OrderingContext, selectedPaymentMethodType?: string) {
  const { totalWithoutCredit } = prepareOrderingLedger(context);

  const { products, productIds, bagCount, hasSharedLineItem, hasBundleLineItem } =
    formatTelemetryProducts(context);

  const address = context.cart?.deliveryOrderDetail?.address;

  const isGroupOrder = context.cart?.isGroupOrder;
  const groupOrderParticipantCount = isGroupOrder
    ? getParticipantLineItemGroups(context.cart.lineItems ?? []).length
    : undefined;

  const minutesToEarliestWantedTime = calculateMinutesToEarliestWantedTime(context);

  return {
    orderId: context.cart?.orderId ?? context.orderId,
    orderValue: convertPriceToFloat(totalWithoutCredit),
    products,
    productIds,
    bagCount,
    hasSharedLineItem,
    hasBundleLineItem,
    isGroupOrder,
    groupOrderParticipantCount,
    restaurantId: context.cart?.restaurantId,
    restaurantName: context.cart?.locationName,
    orderType: context.cart?.orderChannel,
    street: address?.street,
    city: address?.city,
    state: address?.state,
    zipCode: address?.zipCode,
    country: address?.country,
    customer: formatTelemetryCustomer(context.customer),
    isNewCustomer: context.customer.isNewCustomer,
    selectedPaymentMethodType,
    minutesToEarliestWantedTime,
  };
}

// Used by `ga.view_cart` and `ga.begin_checkout`. Google only wants order value of line items
// without order credit, tax, etc
export function formatBagForGoogleAnalytics(
  context: OrderingContext,
): TelemetryEvent<'ga.begin_checkout'>['payload'] {
  const data = formatTelemetryBag(context);

  const orderValue = data.products?.reduce(
    (total, { cost = 0, quantity = 0 }) => total + cost * quantity,
    0,
  );

  return { ...data, orderValue };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatTelemetryCustomer(customer: OrderingContext['customer']) {
  return {
    firstName: customer.firstName,
    lastName: customer.lastName,
    email: customer.email,
    phoneNumber: customer.phoneNumber,
  };
}

function formatTelemetryProducts(context: OrderingContext) {
  const sharedLineItemAttribution = getSharedLineItemAttribution();
  const bundleAttribution = getBundleAttribution();

  const products = context.cart.lineItems?.map(
    ({ id: lineItemId, product, perItemCost, quantity, customName }) => {
      const sharedLineItemTelemetry = getSharedLineItemCartLineItemTelemetry({
        attribution: sharedLineItemAttribution,
        cartLineItemId: lineItemId,
      });

      const isBundleLineItem = checkIfBundleCartLineItem({
        attribution: bundleAttribution,
        cartLineItemId: lineItemId,
      });

      return telemetryProductFromInput({
        ...product,
        ...sharedLineItemTelemetry,
        isBundleLineItem,
        restaurantId: context.cart?.restaurantId,
        location: context.cart?.locationName,
        cost: perItemCost,
        quantity,
        name: customName ?? product?.name,
      });
    },
  );

  const productIds = products?.map(({ id }) => id).join(',');
  const bagCount = sumProductQuantities(products);
  const hasSharedLineItem = products?.some(({ isSharedLineItem }) => Boolean(isSharedLineItem));
  const hasBundleLineItem = products?.some(({ isBundleLineItem }) => Boolean(isBundleLineItem));

  return {
    products,
    productIds,
    bagCount,
    hasSharedLineItem,
    hasBundleLineItem,
  };
}

function prepareOrderingLedger(context: OrderingContext) {
  return prepareLedger({
    ledger: context.cart?.ledger,
    tip: context.selectedTip,
    availableCredit: context.availableCredit,
    shouldUseCredit: context.shouldUseCredit,
    amountEligibleForFlex: context.amountEligibleForFlex,
    shouldUseFlex: context.shouldUseFlex,
  });
}

function sumProductQuantities(products?: readonly { quantity?: number }[]) {
  return products?.reduce((total, product) => total + (product.quantity ?? 0), 0) ?? 0;
}

function calculateMinutesToEarliestWantedTime(context: OrderingContext) {
  const earliestWantedTime = context.cart?.wantedTimes?.[0];

  if (!earliestWantedTime) return undefined;

  const now = new Date();
  const earliestWantedTimeDate = adjustWantedTime(earliestWantedTime);

  if (!earliestWantedTimeDate) return undefined;

  return differenceInMinutes(earliestWantedTimeDate, now);
}
